import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';

import Header from './components/common/Header';
import Sidebar from './components/common/Sidebar';
import Loader from './components/common/Loader';
import ErrorBoundary from './components/common/ErrorBoundary';

import DirectorDashboard from './components/dashboard/DirectorDashboard';
import TeacherDashboard from './components/dashboard/TeacherDashboard';
import PipelineJobsPage from './components/dashboard/PipelineJobsPage';

import UserList from './components/user/UserList';
import AddUser from './components/user/AddUser';
import EditUser from './components/user/EditUser';
import ViewUser from './components/user/ViewUser';

import SchoolList from './components/school/SchoolList';
import AddSchool from './components/school/AddSchool';
import EditSchool from './components/school/EditSchool';
import ViewSchool from './components/school/ViewSchool';

import StudentList from './components/student/StudentList';
import AddStudent from './components/student/AddStudent';
import EditStudent from './components/student/EditStudent';
import ViewStudent from './components/student/ViewStudent';
import StudentComplianceReport from './components/student/StudentComplianceReport';
import IepComplianceChecklist from './components/student/IepComplianceChecklist';
import StudentEligibilityChecklist from './components/student/StudentEligibilityChecklist';
import AssessmentHistory from './components/student/AssessmentHistory';
import ViewFileCabinet from './components/student/fileCabinet/ViewFileCabinet';
import AddFileCabinet from './components/student/fileCabinet/AddFileCabinet';
import StudentJobsDashboard from './components/student/StudentJobsDashboard';

import EligibilityTemplateList from './components/admin/EligibilityTemplateList';
import EligibilityTemplateEditor from './components/admin/EligibilityTemplateEditor';

import BackdoorPage from './components/backdoor/BackdoorPage';

export function getDashboardPath(role) {
  if (role === 1) return '/director-dashboard';
  if (role === 2) return '/teacher-dashboard';
  return '/login';
}

const NAV_COLLAPSED_KEY = 'athenian.navCollapsed';

function readNavCollapsed() {
  try {
    return localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

// `compact` pages (the student report and its checklists) bring their own left panel:
// the main nav stays as the icon rail and expands over the page instead of beside it.
// `bare` pages lay out their own padding.
function ProtectedRoute({ children, compact = false, bare = false }) {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [savedCollapsed, setNavCollapsed] = useState(readNavCollapsed);
  const navCollapsed = compact || savedCollapsed;

  const toggleNav = () => {
    setNavCollapsed((prev) => {
      try {
        localStorage.setItem(NAV_COLLAPSED_KEY, prev ? '0' : '1');
      } catch {
        // Remembering the choice is a convenience only.
      }
      return !prev;
    });
  };

  if (loading) return <Loader fullScreen text="Loading..." />;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#f6f7fb] dark:bg-slate-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={navCollapsed}
        onToggleCollapse={compact ? () => setSidebarOpen(true) : toggleNav}
        overlayOnDesktop={compact}
      />
      <Header withSidebar sidebarCollapsed={navCollapsed} onMenuClick={() => setSidebarOpen(prev => !prev)} />
      {/* overflow-x-clip, not hidden: hidden would make <main> a scroll container and
          break the report panel's position: sticky. */}
      <main className={`${navCollapsed ? 'lg:ml-[68px]' : 'lg:ml-[248px]'} pt-16 min-h-screen min-w-0 overflow-x-clip transition-[margin] duration-300`}>
        <ErrorBoundary>
          {bare ? children : (
            <div className="mx-auto w-full min-w-0 max-w-[100%] px-3 py-4 sm:px-4 sm:py-5 md:px-5 lg:px-6 xl:px-8 2xl:max-w-[min(100%,1800px)]">
              {children}
            </div>
          )}
        </ErrorBoundary>
      </main>
    </div>
  );
}

function StudentReportRoute({ children }) {
  return <ProtectedRoute compact bare>{children}</ProtectedRoute>;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to={getDashboardPath(user.role)} replace /> : children;
}

function DashboardRedirect() {
  const { user } = useAuth();
  return <Navigate to={getDashboardPath(user?.role)} replace />;
}

// Standalone internal route: requires login, no sidebar/header, gated by PIN inside.
function BackdoorRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader fullScreen text="Loading..." />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader fullScreen text="Loading..." />;
  if (!user || !roles.includes(user.role)) {
    return <Navigate to={getDashboardPath(user?.role)} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Dashboard */}
      <Route path="/director-dashboard" element={
        <ProtectedRoute><RequireRole roles={[1]}><DirectorDashboard /></RequireRole></ProtectedRoute>
      } />
      <Route path="/pipeline-jobs" element={
        <ProtectedRoute><RequireRole roles={[1, 2]}><PipelineJobsPage /></RequireRole></ProtectedRoute>
      } />
      <Route path="/teacher-dashboard" element={
        <ProtectedRoute><RequireRole roles={[2]}><TeacherDashboard /></RequireRole></ProtectedRoute>
      } />

      {/* Users */}
      <Route path="/users" element={
        <ProtectedRoute><RequireRole roles={[1]}><UserList /></RequireRole></ProtectedRoute>
      } />
      <Route path="/users/add" element={
        <ProtectedRoute><RequireRole roles={[1]}><AddUser /></RequireRole></ProtectedRoute>
      } />
      <Route path="/users/:id" element={
        <ProtectedRoute><RequireRole roles={[1]}><ViewUser /></RequireRole></ProtectedRoute>
      } />
      <Route path="/users/:id/edit" element={
        <ProtectedRoute><RequireRole roles={[1, 2]}><EditUser /></RequireRole></ProtectedRoute>
      } />

      {/* Schools (directors only) */}
      <Route path="/schools" element={
        <ProtectedRoute><RequireRole roles={[1]}><SchoolList /></RequireRole></ProtectedRoute>
      } />
      <Route path="/schools/add" element={
        <ProtectedRoute><RequireRole roles={[1]}><AddSchool /></RequireRole></ProtectedRoute>
      } />
      <Route path="/schools/:id" element={
        <ProtectedRoute><RequireRole roles={[1]}><ViewSchool /></RequireRole></ProtectedRoute>
      } />
      <Route path="/schools/:id/edit" element={
        <ProtectedRoute><RequireRole roles={[1]}><EditSchool /></RequireRole></ProtectedRoute>
      } />

      {/* Admin (directors only) */}
      <Route path="/admin/eligibility-templates" element={
        <ProtectedRoute><RequireRole roles={[1]}><EligibilityTemplateList /></RequireRole></ProtectedRoute>
      } />
      <Route path="/admin/eligibility-templates/:templateId" element={
        <ProtectedRoute><RequireRole roles={[1]}><EligibilityTemplateEditor /></RequireRole></ProtectedRoute>
      } />

      {/* Students */}
    <Route path="/students" element={
      <ProtectedRoute><StudentList /></ProtectedRoute>
    } />
    <Route path="/students/add" element={
      <ProtectedRoute><AddStudent /></ProtectedRoute>
    } />
    <Route path="/students/:id" element={
      <ProtectedRoute><ViewStudent /></ProtectedRoute>
    } />
    <Route path="/students/:id/edit" element={
      <ProtectedRoute><EditStudent /></ProtectedRoute>
    } />
    <Route path="/students/:id/files" element={
      <ProtectedRoute><ViewFileCabinet /></ProtectedRoute>
    } />
    <Route path="/students/:id/files/add" element={
      <ProtectedRoute><AddFileCabinet /></ProtectedRoute>
    } />
    <Route path="/students/:id/jobs" element={
      <ProtectedRoute><StudentJobsDashboard /></ProtectedRoute>
    } />
    <Route path="/students/:id/information" element={
      <StudentReportRoute><StudentComplianceReport /></StudentReportRoute>
    } />
    <Route path="/students/:id/information/section/:sectionKey" element={
      <StudentReportRoute><StudentComplianceReport /></StudentReportRoute>
    } />
    <Route path="/students/:id/iep-checklist" element={
      <StudentReportRoute><IepComplianceChecklist /></StudentReportRoute>
    } />
    <Route path="/students/:id/eligibility_checklist" element={
      <StudentReportRoute><StudentEligibilityChecklist /></StudentReportRoute>
    } />
    <Route path="/students/:id/assessment-history" element={
      <StudentReportRoute><AssessmentHistory /></StudentReportRoute>
    } />

      {/* Internal monitoring (PIN-gated, no sidebar, not in nav) */}
      <Route path="/backdoor" element={<BackdoorRoute><BackdoorPage /></BackdoorRoute>} />

      <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider> 
      </ThemeProvider>
    </BrowserRouter>
  );
}
